import { baseUrl } from '../../env';
import { DateTime } from 'luxon';
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { proposerLabel } from '@/utils/proposerCategories';

export default {
    name: 'QueryPage',
    components: {
        VueDatePicker,
        VueSelect
    },

    data() {
        return {
            // Date range (form state)
            date: { from: null, to: null },

            // Table/result data
            filteredData: [],
            data: null,

            // UI state
            loading: false,
            sortKey: 0,
            sortAsc: true,

            // Members (for query 22)
            members: [],
            memberName: null,

            // Query separation
            selectedQuery: null,
            appliedQuery: null,

            // misc
            lastQuery: null,

            // totals kept as state (computed must not mutate it)
            totals: { totalCount: 0, bothCount: 0, suggestedOnlyCount: 0, chosenOnlyCount: 0 },

            initialVisible: 5,
            contestsExpanded: {},
            nonContestsExpanded: {},
            townshipExpanded: {}
        };
    },

    created() {
        try {
            const upitFromRoute = this.$route?.query?.upit || '1';
            this.selectedQuery = upitFromRoute;
            this.appliedQuery = upitFromRoute;

            this.date.from = this.$route?.query?.od
                ? DateTime.fromFormat(this.$route.query.od, 'dd.MM.yyyy.').toJSDate()
                : null;

            this.date.to = this.$route?.query?.do
                ? DateTime.fromFormat(this.$route.query.do, 'dd.MM.yyyy.').toJSDate()
                : null;

            this.memberName = this.$route?.query?.memberName || null;

            // Load members for dropdown
            this.fetchMembers();

            // Load initial data (based on appliedQuery)
            if (this.appliedQuery) {
                const initialFrom = this.date.from
                    ? DateTime.fromJSDate(this.date.from).toFormat('dd.MM.yyyy.')
                    : undefined;
                const initialTo = this.date.to
                    ? DateTime.fromJSDate(this.date.to).toFormat('dd.MM.yyyy.')
                    : undefined;

                this.fetchData({
                    upit: this.appliedQuery,
                    from: initialFrom,
                    to: initialTo,
                    memberName: this.memberName || undefined
                });
            }
        } catch (err) {
            console.error('Error during created():', err);
        }
    },

    computed: {
        showTotal() {
            return (
                this.data &&
                typeof this.data.count !== 'undefined' &&
                !['11', '15', '16'].includes(this.appliedQuery)
            );
        },

        currentTableHeaders() {
            if (this.appliedQuery === '22') {
                return ['Име и презиме', 'Није изабран/а - Изабран/а', 'На чији предлог', 'Датум', 'Општина', 'Конкурс', 'Изабрани чланови комисије'];
            }
            if (this.appliedQuery === '21') {
                return ['Јединица власти', 'Датум конкурса'];
            }
            if (['11', '15', '16'].includes(this.appliedQuery)) {
                return ['Статус', 'Број'];
            }
            if (['6', '7', '8', '9', '10', '12', '13', '14'].includes(this.appliedQuery)) {
                return ['Назив', 'Износ', 'Konkurs'];
            }
            if (['2'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно конкурса', 'Конкурси'];
            }
            if (['19'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно именованих', 'Јединица - Конкурс'];
            }
            if (['20'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно именованих', 'Конкурси'];
            }
            if (['1', '3'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно конкурса', 'Укупно средстава', 'Додељено средстава', 'Конкурси'];
            }
            if (['4'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно пројеката', 'Укупно средстава', 'Додељено средстава', 'Конкурси'];
            }
            if (['5'].includes(this.appliedQuery)) {
                return ['Назив', 'Укупно пројеката', 'Укупно конкурса', 'Укупно средстава', 'Додељено средстава', 'Конкурси'];
            }
            return ['Назив', 'Вредност'];
        },

        currentTableHeaders2() {
            if (['11', '15', '16'].includes(this.appliedQuery)) {
                return ['Назив', 'Примедбе'];
            }
            return [];
        },

        // formattedTableData returns a safe, well-typed array for rendering.
        formattedTableData() {
            try {
                if (!this.data) return [];

                // Helper to safely parse a dd.MM.yyyy. string into a Date object
                const safeParseDate = (d) => {
                    if (!d || typeof d !== 'string') return new Date(0);
                    const parts = d.split('.').filter(Boolean);
                    if (parts.length < 3) return new Date(0);
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10);
                    const year = parseInt(parts[2], 10);
                    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return new Date(0);
                    return new Date(year, month - 1, day);
                };

                // QUERY 22 — special flattening by members
                if (this.appliedQuery === '22') {
                    const raw = Array.isArray(this.filteredData) ? this.filteredData : [];

                    // 1) Flatten commission members safely
                    const expanded = raw.flatMap(item => {
                        let members = [];
                        try {
                            members =
                                typeof item.commissionMembers === 'string'
                                    ? JSON.parse(item.commissionMembers || '[]')
                                    : Array.isArray(item.commissionMembers)
                                        ? item.commissionMembers
                                        : [];
                        } catch {
                            members = [];
                        }

                        // contests without members come back as one all-null member (LEFT JOIN), skip it
                        return members.filter(m => m && m.id != null).map(m => ({
                            memberName: (m.memberName || m.name || '').trim(),
                            chosen: Number(m.chosen) || 0,
                            suggested: Number(m.suggested) || 0,
                            proposer: proposerLabel(m.proposerCategory, m.proposerOrganization),

                            // important fields for grouping/sorting
                            calendar: item.calendar || '',
                            townshipName: item.townshipName || item.township?.name || '',
                            competition_name: item.competition_name || '',
                            // drop blank entries left by empty members (e.g. "Ime, \t, Ime")
                            commission: (item.commission || '').split(',').map(n => n.trim()).filter(Boolean).join(', '),
                            contestId: item.contestId || null,
                            townshipId: item.townshipId || null,
                            id: item.id || null
                        }));
                    });

                    // 2) GROUP BY townshipId + contestId + member, so the proposer belongs to the shown member
                    const groupedObj = expanded.reduce((acc, m) => {
                        const key = `${m.townshipId || 'unknown'}_${m.contestId || 'noContest'}_${m.memberName}`;

                        if (!acc[key]) {
                            acc[key] = {
                                townshipId: m.townshipId,
                                contestId: m.contestId,
                                calendar: m.calendar,
                                townshipName: m.townshipName,
                                competition_name: m.competition_name,
                                commission: m.commission,
                                id: m.id,

                                chosen: 0,
                                suggested: 0,
                                memberNames: new Set(),
                                proposers: new Set()
                            };
                        }

                        // Unique member names
                        if (m.memberName) {
                            acc[key].memberNames.add(m.memberName);
                        }
                        acc[key].proposers.add(m.proposer);

                        // Status logic
                        if (m.chosen === 1) {
                            acc[key].chosen = 1;
                            acc[key].suggested = 1;
                        } else if (m.suggested === 1 && acc[key].chosen === 0) {
                            acc[key].suggested = 1;
                        }

                        return acc;
                    }, {});

                    const grouped = Object.values(groupedObj);

                    // convert Set to Array and compute members_count
                    grouped.forEach(g => {
                        g.memberNames = [...g.memberNames];
                        g.memberName = g.memberNames[0] || '';
                        g.members_count = g.memberNames.length;
                        g.proposer = [...g.proposers].join(', ');
                    });

                    // 3) Column mapping for sort index
                    const keys = [
                        'memberName',
                        'status',
                        'proposer',
                        'calendar',
                        'townshipName',
                        'competition_name',
                        'commission',
                        'members_count'
                    ];

                    const sortIndex = Number.isFinite(this.sortKey) ? this.sortKey : null;
                    const direction = this.sortAsc ? 1 : -1;

                    // 4) Sorting
                    if (sortIndex !== null && sortIndex >= 0 && sortIndex < keys.length) {
                        const key = keys[sortIndex];

                        grouped.sort((a, b) => {
                            let va = a[key], vb = b[key];

                            if (key === 'calendar') {
                                va = safeParseDate(String(a.calendar || ''));
                                vb = safeParseDate(String(b.calendar || ''));
                                return (va - vb) * direction;
                            }
                            if (key === 'status') {
                                const score = x => (x.chosen ? 2 : x.suggested ? 1 : 0);
                                return (score(a) - score(b)) * direction;
                            }
                            if (key === 'members_count') {
                                return (a.members_count - b.members_count) * direction;
                            }

                            return String(va || '').localeCompare(String(vb || ''), 'sr') * direction;
                        });
                    }

                    return grouped;
                }

                if (this.appliedQuery === '21' && Array.isArray(this.filteredData)) {
                    const cancelledContests = this.filteredData.map(contest => ({
                        id: contest.contestid ?? contest.id ?? null,
                        contestId: contest.contestid ?? contest.id ?? null,
                        name: contest.township?.name || contest.competition_name || '',
                        calendar: contest.calendar || ''
                    }));
                    return this.onSort(cancelledContests);
                }

                // Remarks queries
                if (['11', '15', '16'].includes(this.appliedQuery) && this.data?.data) {
                    const remarksGrouped = this.groupRemarksByTownship(Array.isArray(this.data.data.remarks) ? this.data.data.remarks : []);
                    const remarksTable = Object.values(remarksGrouped).map(township => ({
                        name: township.townshipName,
                        id: township.townshipId,
                        remarkTypes: Object.values(township.remarkTypes),
                        expanded: false
                    }));
                    // dataToFormat (buckets)
                    const dataToFormat = [
                        { status: 'Уважене', value: Number(this.data.data.accepted || 0) },
                        { status: 'Делимично уважене', value: Number(this.data.data.partially || 0) },
                        { status: 'Нису уважене', value: Number(this.data.data.declined || 0) }
                    ];
                    // sorting for remarks table can be handled elsewhere
                    return { dataToFormat, remarksTable };
                }

                // queries 17/18 simple mapping
                if (['17', '18'].includes(this.appliedQuery) && this.data?.data) {
                    const entries = Object.entries(this.data.data || {}).filter(([name, count]) => name && typeof count !== 'undefined');
                    return entries.map(([name, count]) => ({ name, value: Number(count || 0) }));
                }

                // queries 1,3,4,5 grouped by id
                if (['1', '3', '4', '5'].includes(this.appliedQuery) && Array.isArray(this.data?.data || this.filteredData)) {
                    const source = Array.isArray(this.filteredData) ? this.filteredData : (Array.isArray(this.data.data) ? this.data.data : []);
                    const grouped = this.groupBy(source.filter(item => item && item.name !== undefined), 'id');

                    const mapped = Object.values(grouped).map(group => {
                        const first = group[0] || {};
                        const contests = group.filter(i => i.contestId).map(i => ({ contestId: i.contestId, calendar: i.calendar }));
                        const sortedContests = contests.sort((a, b) =>
                            DateTime.fromFormat(a.calendar || '01.01.1971.', "dd.MM.yyyy").toMillis() - DateTime.fromFormat(b.calendar || '01.01.1971.', "dd.MM.yyyy").toMillis()
                        );

                        const result = {
                            name: first.name || '',
                            count: group.length,
                            contests: sortedContests,
                            value: group.reduce((sum, i) => sum + Number(i.value || i.total_money || 0), 0),
                            id: first.id || null,
                            expanded: !!(group[0] && group[0].expanded)
                        };

                        if (this.appliedQuery === '4' || this.appliedQuery === '5' || this.appliedQuery === '1') {
                            result.projectsTotalCount = group.reduce((sum, i) => sum + Number(i.project_total ?? i.granted_projects ?? 0), 0);
                            result.total_money = group.reduce((sum, i) => sum + Number(i.total_money || 0), 0);
                            result.money_allocated = group.reduce((sum, i) => sum + Number(i.money_allocated || 0), 0);
                        }
                        return result;
                    });

                    // col→key mapping per query
                    // 1,3: Назив(0), УкупноКонкурса(1), УкупноСредстава(2), ДодељеноСредстава(3)
                    // 4:   Назив(0), УкупноПројеката(1), УкупноСредстава(2), ДодељеноСредстава(3)
                    // 5:   Назив(0), УкупноПројеката(1), УкупноКонкурса(2), УкупноСредстава(3), ДодељеноСредстава(4)
                    const colKeys135 = {
                        '1': ['name', 'count', 'total_money', 'money_allocated'],
                        '3': ['name', 'count', 'total_money', 'money_allocated'],
                        '4': ['name', 'projectsTotalCount', 'total_money', 'money_allocated'],
                        '5': ['name', 'projectsTotalCount', 'count', 'total_money', 'money_allocated'],
                    };
                    const keys135 = colKeys135[this.appliedQuery] || ['name'];
                    const k135 = keys135[Number(this.sortKey)] || 'name';
                    const dir135 = this.sortAsc ? 1 : -1;
                    return mapped.sort((a, b) => {
                        const va = a[k135], vb = b[k135];
                        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir135;
                        return String(va ?? '').localeCompare(String(vb ?? ''), 'sr') * dir135;
                    });
                }

                // query 2 grouping
                if (['2'].includes(this.appliedQuery) && Array.isArray(this.data?.data || this.filteredData)) {
                    const source = Array.isArray(this.filteredData) ? this.filteredData : (Array.isArray(this.data.data) ? this.data.data : []);
                    const grouped = this.groupBy(source.filter(item => item && item.name !== undefined), 'id');

                    const mapped = Object.values(grouped).map(group => {
                        const first = group[0] || {};
                        const contests = group.filter(i => i.contestId).map(i => ({ contestId: i.contestId, calendar: i.calendar }))
                            .sort((a, b) => DateTime.fromFormat(b.calendar || '01.01.1971.', "dd.MM.yyyy").toMillis() - DateTime.fromFormat(a.calendar || '01.01.1971.', "dd.MM.yyyy").toMillis());
                        return {
                            name: first.name || '',
                            count: group.length,
                            contests,
                            value: group.length,
                            id: first.id || null,
                            expanded: !!(group[0] && group[0].expanded)
                        };
                    });

                    // Назив(0), УкупноКонкурса(1), Конкурси(2-не сортира)
                    const colKeys2 = ['name', 'count'];
                    const k2 = colKeys2[Number(this.sortKey)] || 'name';
                    const dir2 = this.sortAsc ? 1 : -1;
                    return mapped.sort((a, b) => {
                        const va = a[k2], vb = b[k2];
                        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir2;
                        return String(va ?? '').localeCompare(String(vb ?? ''), 'sr') * dir2;
                    });
                }

                // Default: array of items
                if (Array.isArray(this.filteredData)) {
                    const data = this.filteredData.filter(item => item && (item.name !== undefined || item.name !== null));
                    const mapped = data.map(item => {
                        // compute members_count safely
                        let members_count = 0;
                        if (Array.isArray(item.contests)) {
                            members_count = item.contests.reduce((sum, contest) => {
                                if (contest && contest.township && Array.isArray(contest.township.township_contests)) {
                                    return sum + contest.township.township_contests.reduce((innerSum, tc) => innerSum + (Number(tc.memberCount || 0)), 0);
                                }
                                return sum;
                            }, 0);
                        }
                        return {
                            name: item.name || '',
                            value: Number(item.value || item.total_money || item.money_allocated || item.remain_money || item.cnt || 0),
                            calendar: item.calendar || '',
                            contestId: item.contestId || null,
                            contests: Array.isArray(item.contests) ? item.contests : [],
                            id: item.id ?? null,
                            organization_id: item.organization_id ?? null,
                            members_count
                        };
                    });

                    // default sort for 19 by members_count desc unless another sort is requested
                    if (this.appliedQuery === '19') {
                        // If sortKey mapped to column index, convert safely
                        const direction = this.sortAsc ? 1 : -1;
                        // keys: name (0), value (1), members_count (2)
                        if (Number.isFinite(this.sortKey)) {
                            const idx = Number(this.sortKey);
                            const keys = ['name', 'value', 'members_count'];
                            if (idx >= 0 && idx < keys.length) {
                                const k = keys[idx];
                                if (k === 'members_count') {
                                    return mapped.sort((a, b) => (a.members_count - b.members_count) * direction);
                                } else {
                                    return mapped.sort((a, b) => String(a[k] || '').localeCompare(String(b[k] || ''), 'sr') * direction);
                                }
                            }
                        }
                        // default fallback
                        return mapped.sort((a, b) => (b.members_count - a.members_count));
                    }
                    

                    // Default sort: use onSort if not query 19
                    return this.onSort(mapped);
                }

                // Fallback empty
                return [];
            } catch (err) {
                console.error('formattedTableData error:', err);
                return [];
            }
        }
    },

    methods: {
        sortTable(key) {
            if (this.sortKey === key) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },

        toggleContests(item) {
            if (!item || !item.id) return;
            this.contestsExpanded[item.id] = !this.contestsExpanded[item.id];
        },

        toggleNonContests(item) {
            if (!item || !item.id) return;
            this.nonContestsExpanded[item.id] = !this.nonContestsExpanded[item.id];
        },

        getVisibleContests(item) {
            if (!item || !Array.isArray(item.contests)) return [];
            const isExpanded = !!this.contestsExpanded[item.id];
            return isExpanded ? item.contests : item.contests.slice(0, this.initialVisible);
        },

        getVisibleNonContests(item) {
            if (!item || !Array.isArray(item.contests)) return [];
            const isExpanded = !!this.nonContestsExpanded[item.id];
            return isExpanded ? item.contests : item.contests.slice(0, this.initialVisible);
        },

        getVisibleTownships(item) {
            if (!item || !Array.isArray(item.contests)) return [];
            const id = item.id ?? 'unknown';
            const isExpanded = !!this.townshipExpanded[id];
            return isExpanded ? item.contests : item.contests.slice(0, 5);
        },

        toggleTownship(township) {
            if (!township || (typeof township.id === 'undefined' || township.id === null)) return;
            const id = township.id;
            this.townshipExpanded[id] = !this.townshipExpanded[id];
        },

        getVisibleTownshipContests(township) {
            if (!township || !Array.isArray(township.township_contests)) return [];
            const isExpanded = !!this.townshipExpanded[township.id];
            return isExpanded ? township.township_contests : township.township_contests.slice(0, 5);
        },

        memberStatus(chosen, suggested) {
            if (Number(chosen) === 1) return 'Изабран/а';
            if (Number(suggested) === 1 && Number(chosen) === 0) return 'Није изабран/а';
            return '';
        },

        async fetchData(opts = {}) {
            this.loading = true;
            try {
                const upit = opts.upit || this.appliedQuery || '1';
                const from = opts.from || (this.date.from ? DateTime.fromJSDate(this.date.from).toFormat('dd.MM.yyyy.') : '01.01.1971.');
                const to = opts.to || (this.date.to ? DateTime.fromJSDate(this.date.to).toFormat('dd.MM.yyyy.') : DateTime.local().toFormat('dd.MM.yyyy.'));

                let memberQuery = '';
                if (opts.memberName) {
                    memberQuery = Array.isArray(opts.memberName) ? opts.memberName.join(',') : String(opts.memberName).trim();
                } else if (this.memberName) {
                    memberQuery = Array.isArray(this.memberName) ? this.memberName.join(',') : String(this.memberName).trim();
                }

                // Build URL safely
                let url = `${baseUrl}/query/${encodeURIComponent(upit)}/${encodeURIComponent(from)}/${encodeURIComponent(to)}`;
                if (memberQuery) url += `?memberName=${encodeURIComponent(memberQuery)}`;

                if (upit === '21') {
                    url = `${baseUrl}/cancelled-contests/${encodeURIComponent(from)}/${encodeURIComponent(to)}`;
                }

                const response = await axios.get(url);
                let payload = response?.data ?? null;

                if (upit === '21') {
                    if (!Array.isArray(payload)) {
                        throw new Error('Unexpected response while fetching cancelled contests.');
                    }
                    const cancelledContests = payload.filter(contest => {
                        const value = contest.competition_cancelled;
                        return value === true || value === 1 || ['1', 'true', 'yes', 'da'].includes(String(value).trim().toLowerCase());
                    });
                    payload = { count: cancelledContests.length, data: cancelledContests };
                }

                this.data = payload;

                // decide filteredData
                let nextFiltered = [];
                if (payload) {
                    if (Array.isArray(payload.data)) nextFiltered = payload.data;
                    else if (Array.isArray(payload.filteredData)) nextFiltered = payload.filteredData;
                    else if (Array.isArray(payload)) nextFiltered = payload;
                }
                this.filteredData = nextFiltered;

                // OPTIONAL: update totals reactive state here if needed
                // e.g. compute totals for query 22 or 19 here and assign this.totals
                // Keep totals mutation outside computed (computed must be pure)
                if (this.appliedQuery === '22') {
                    // Example: compute totals based on filteredData
                    try {
                        const expanded = nextFiltered.flatMap(item => {
                            try {
                                return typeof item.commissionMembers === 'string' ? JSON.parse(item.commissionMembers || '[]') : (Array.isArray(item.commissionMembers) ? item.commissionMembers : []);
                            } catch { return []; }
                        });
                        const chosenOnly = expanded.filter(m => Number(m.chosen) === 1).length;
                        const suggestedOnly = expanded.filter(m => Number(m.suggested) === 1 && Number(m.chosen) === 0).length;
                        this.totals = {
                            totalCount: chosenOnly + suggestedOnly,
                            suggestedOnlyCount: suggestedOnly,
                            chosenOnlyCount: chosenOnly
                        };
                    } catch (e) {
                        // keep previous totals if something goes wrong
                    }
                }
                if (this.appliedQuery === '19') {
                    try {
                        // Sum all memberCount values from contests → township_contests
                        const totalMembers = nextFiltered.reduce((sum, item) => {
                            if (Array.isArray(item.contests)) {
                                const contestSum = item.contests.reduce((cSum, contest) => {
                                    if (contest.township && Array.isArray(contest.township.township_contests)) {
                                        return cSum + contest.township.township_contests.reduce((tcSum, tc) => {
                                            return tcSum + Number(tc.memberCount || 0);
                                        }, 0);
                                    }
                                    return cSum;
                                }, 0);
                                return sum + contestSum;
                            }
                            return sum;
                        }, 0);

                        // Override data.count with computed sum
                        if (this.data) {
                            this.data.count = totalMembers;
                        }
                    } catch (e) {
                        console.error('Error computing total memberCount for query 19:', e);
                    }
                }

                this.lastQuery = upit;
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                this.loading = false;
            }
        },

        async fetchMembers() {
            try {
                const response = await axios.get(`${baseUrl}/commission_members_all`);
                const rows = Array.isArray(response?.data) ? response.data : [];
                this.members = rows.map(m => ({ memberName: m.member_name || m.name || '', label: m.member_name || m.name || '' }));
            } catch (error) {
                console.error('Error fetching members:', error);
                this.members = [];
            }
        },

        submit() {
            this.appliedQuery = this.selectedQuery;
            this.sortKey = 0;
            this.sortAsc = true;
            const from = this.date.from ? DateTime.fromJSDate(this.date.from).toFormat('dd.MM.yyyy.') : undefined;
            const to = this.date.to ? DateTime.fromJSDate(this.date.to).toFormat('dd.MM.yyyy.') : undefined;
            const member = Array.isArray(this.memberName) ? (this.memberName.length > 0 ? this.memberName : undefined) : (this.memberName && String(this.memberName).trim() !== '' ? String(this.memberName).trim() : undefined);

            const query = { upit: this.appliedQuery };
            if (from) query.od = from;
            if (to) query.do = to;
            if (member) query.memberName = Array.isArray(member) ? member.join(',') : member;

            this.$router.push({ query });

            this.fetchData({
                upit: this.appliedQuery,
                from,
                to,
                memberName: member
            });
        },

        formatValue(value) {
            if (typeof value === 'number') return value.toLocaleString('sr');
            if (value == null) return '—';
            return String(value);
        },

        formatMoney(value) {
            if (!value) return '0 RSD';
            return Number(value).toLocaleString('sr-RS') + ' RSD';
        },

        groupBy(array, key) {
            if (!Array.isArray(array)) return {};
            return array.reduce((result, item) => {
                const groupKey = item?.[key] ?? 'undefined';
                if (!result[groupKey]) result[groupKey] = [];
                result[groupKey].push(item);
                return result;
            }, {});
        },

        groupRemarksByTownship(contests) {
            const result = {};
            if (!Array.isArray(contests)) return result;

            contests.forEach(contest => {
                const townshipId = contest?.id ?? 'unknown';
                const id = contest?.contestId ?? 'unknown';
                const townshipName = contest?.name ?? '';

                const remarkField = contest?.remarks_accepted;
                const hasRemark = contest?.some_remarks === "Da";

                if (remarkField && hasRemark) {
                    let remarkType = null;
                    if (remarkField === "Uvažene") remarkType = "Уважене";
                    if (remarkField === "Delimično uvažene") remarkType = "Делимично уважене";
                    if (remarkField === "Nisu uvažene") remarkType = "Нису уважене";

                    if (!remarkType) return;

                    if (!result[townshipId]) {
                        result[townshipId] = { townshipId, townshipName, remarkTypes: {} };
                    }

                    if (!result[townshipId].remarkTypes[remarkType]) {
                        result[townshipId].remarkTypes[remarkType] = { type: remarkType, count: 0, contests: {} };
                    }

                    result[townshipId].remarkTypes[remarkType].count += 1;
                    result[townshipId].remarkTypes[remarkType].contests[id] = { contestId: id, contestName: contest?.calendar ?? '' };
                }
            });

            return result;
        },

        onSort(dataToFormat) {
            // Default queries (6,7,8,9,10,12,13,14): headers ['Назив','Износ','Konkurs']
            // col 0 → name, col 1 → value, col 2 → calendar
            try {
                if (!Array.isArray(dataToFormat) || dataToFormat.length === 0) return dataToFormat;

                const colKeys = this.appliedQuery === '21'
                    ? ['name', 'calendar']
                    : ['name', 'value', 'calendar'];
                const key = colKeys[Number(this.sortKey)] || 'name';
                const asc = !!this.sortAsc;

                dataToFormat.sort((a, b) => {
                    let valA = a?.[key];
                    let valB = b?.[key];

                    if (key === 'calendar' && typeof valA === 'string' && typeof valB === 'string') {
                        const parseDate = (d) => {
                            if (!d) return new Date(0);
                            const parts = d.split('.').filter(Boolean);
                            if (parts.length < 3) return new Date(0);
                            return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                        };
                        valA = parseDate(valA);
                        valB = parseDate(valB);
                    }

                    if (typeof valA === 'number' && typeof valB === 'number') {
                        return asc ? (valA - valB) : (valB - valA);
                    }
                    if (valA instanceof Date && valB instanceof Date) {
                        return asc ? (valA - valB) : (valB - valA);
                    }

                    const cmp = String(valA ?? '').localeCompare(String(valB ?? ''), 'sr');
                    return asc ? cmp : -cmp;
                });
            } catch (err) {
                console.error('onSort error:', err);
            }
            return dataToFormat;
        }
    }
};